import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button/Button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Progress } from '../../../components/ui/Progress';
import { 
  TrendingUp, 
  TrendingDown, 
  Mail, 
  Eye, 
  MousePointer, 
  MessageSquare, 
  Target,
  Users,
  DollarSign,
  Clock,
  AlertTriangle,
  CheckCircle,
  BarChart3,
  PieChart,
  Activity,
  Zap
} from 'lucide-react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

interface CampaignMetric {
  id: string;
  name: string;
  status: 'draft' | 'scheduled' | 'sending' | 'sent' | 'paused' | 'completed';
  metrics: {
    sent: number;
    delivered: number;
    opened: number;
    clicked: number;
    replied: number;
    converted: number;
    unsubscribed: number;
    bounced: number;
  };
  rates: {
    deliveryRate: number;
    openRate: number;
    clickRate: number;
    replyRate: number;
    conversionRate: number;
    unsubscribeRate: number;
    bounceRate: number;
  };
  roi: {
    cost: number;
    revenue: number;
    profit: number;
    roiPercentage: number;
  };
  timeline: {
    createdAt: string;
    sentAt?: string;
    completedAt?: string;
  };
  engagement: {
    totalEngagements: number;
    engagementVelocity: number;
    peakEngagementTime: string;
  };
}

interface CampaignMetricsProps {
  campaignId?: string;
  campaigns?: CampaignMetric[];
  showComparison?: boolean;
  realTimeUpdates?: boolean;
}

const CampaignMetrics: React.FC<CampaignMetricsProps> = ({
  campaignId,
  campaigns = [],
  showComparison = false,
  realTimeUpdates = true
}) => {
  const [selectedCampaign, setSelectedCampaign] = useState<CampaignMetric | null>(null);
  const [timeframe, setTimeframe] = useState<'24h' | '7d' | '30d' | '90d'>('7d');
  const [viewMode, setViewMode] = useState<'overview' | 'detailed' | 'comparison'>('overview');
  const [isLoading, setIsLoading] = useState(false);

  // Mock data for demonstration
  const mockCampaigns: CampaignMetric[] = [
    {
      id: 'camp-1',
      name: 'Q4 Product Launch',
      status: 'completed',
      metrics: {
        sent: 10000,
        delivered: 9800,
        opened: 2940,
        clicked: 420,
        replied: 84,
        converted: 25,
        unsubscribed: 12,
        bounced: 200
      },
      rates: {
        deliveryRate: 98.0,
        openRate: 30.0,
        clickRate: 14.3,
        replyRate: 2.9,
        conversionRate: 0.26,
        unsubscribeRate: 0.12,
        bounceRate: 2.0
      },
      roi: {
        cost: 500,
        revenue: 12500,
        profit: 12000,
        roiPercentage: 2400
      },
      timeline: {
        createdAt: '2024-01-01T10:00:00Z',
        sentAt: '2024-01-02T10:00:00Z',
        completedAt: '2024-01-02T18:00:00Z'
      },
      engagement: {
        totalEngagements: 3444,
        engagementVelocity: 12.5,
        peakEngagementTime: '2024-01-02T11:30:00Z'
      }
    },
    {
      id: 'camp-2',
      name: 'Holiday Promotion',
      status: 'completed',
      metrics: {
        sent: 8500,
        delivered: 8330,
        opened: 2499,
        clicked: 375,
        replied: 67,
        converted: 19,
        unsubscribed: 8,
        bounced: 170
      },
      rates: {
        deliveryRate: 98.0,
        openRate: 30.0,
        clickRate: 15.0,
        replyRate: 2.7,
        conversionRate: 0.23,
        unsubscribeRate: 0.10,
        bounceRate: 2.0
      },
      roi: {
        cost: 425,
        revenue: 9500,
        profit: 9075,
        roiPercentage: 2135
      },
      timeline: {
        createdAt: '2023-12-01T10:00:00Z',
        sentAt: '2023-12-15T10:00:00Z',
        completedAt: '2023-12-15T16:00:00Z'
      },
      engagement: {
        totalEngagements: 2941,
        engagementVelocity: 15.2,
        peakEngagementTime: '2023-12-15T12:00:00Z'
      }
    }
  ];

  const activeCampaigns = campaigns.length > 0 ? campaigns : mockCampaigns;
  const currentCampaign = selectedCampaign || activeCampaigns[0];

  useEffect(() => {
    if (campaignId) {
      const campaign = activeCampaigns.find(c => c.id === campaignId);
      setSelectedCampaign(campaign || null);
    }
  }, [campaignId, activeCampaigns]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-500';
      case 'sending': return 'bg-blue-500';
      case 'scheduled': return 'bg-yellow-500';
      case 'paused': return 'bg-orange-500';
      case 'draft': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusText = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercentage = (rate: number) => {
    return `${rate.toFixed(1)}%`;
  };

  // Generate time series data for charts
  const generateTimeSeriesData = () => {
    const data = [];
    const now = new Date();
    
    for (let i = 6; i >= 0; i--) {
      const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
      data.push({
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        opens: Math.floor(Math.random() * 500) + 200,
        clicks: Math.floor(Math.random() * 100) + 30,
        replies: Math.floor(Math.random() * 20) + 5,
        conversions: Math.floor(Math.random() * 5) + 1
      });
    }
    
    return data;
  };

  const timeSeriesData = generateTimeSeriesData();

  const pieChartData = [
    { name: 'Opened', value: currentCampaign.metrics.opened, color: '#3B82F6' },
    { name: 'Clicked', value: currentCampaign.metrics.clicked, color: '#10B981' },
    { name: 'Replied', value: currentCampaign.metrics.replied, color: '#F59E0B' },
    { name: 'Converted', value: currentCampaign.metrics.converted, color: '#EF4444' },
    { name: 'Unengaged', value: currentCampaign.metrics.delivered - currentCampaign.metrics.opened, color: '#6B7280' }
  ];

  const MetricCard = ({ 
    title, 
    value, 
    change, 
    icon: Icon, 
    color = 'blue',
    format = 'number'
  }: {
    title: string;
    value: number;
    change?: number;
    icon: any;
    color?: string;
    format?: 'number' | 'percentage' | 'currency';
  }) => {
    const formatValue = () => {
      switch (format) {
        case 'percentage': return formatPercentage(value);
        case 'currency': return formatCurrency(value);
        default: return formatNumber(value);
      }
    };

    return (
      <Card className="hover:shadow-lg transition-shadow">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{title}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatValue()}</p>
              {change !== undefined && (
                <div className="flex items-center mt-1">
                  {change > 0 ? (
                    <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
                  ) : (
                    <TrendingDown className="h-4 w-4 text-red-500 mr-1" />
                  )}
                  <span className={`text-sm ${change > 0 ? 'text-green-500' : 'text-red-500'}`}>
                    {Math.abs(change).toFixed(1)}%
                  </span>
                </div>
              )}
            </div>
            <div className={`p-3 rounded-full bg-${color}-100 dark:bg-${color}-900/20`}>
              <Icon className={`h-6 w-6 text-${color}-600 dark:text-${color}-400`} />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const OverviewTab = () => (
    <div className="space-y-6">
      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total Sent"
          value={currentCampaign.metrics.sent}
          change={5.2}
          icon={Mail}
          color="blue"
        />
        <MetricCard
          title="Open Rate"
          value={currentCampaign.rates.openRate}
          change={2.1}
          icon={Eye}
          color="green"
          format="percentage"
        />
        <MetricCard
          title="Click Rate"
          value={currentCampaign.rates.clickRate}
          change={-0.5}
          icon={MousePointer}
          color="purple"
          format="percentage"
        />
        <MetricCard
          title="Conversion Rate"
          value={currentCampaign.rates.conversionRate}
          change={1.2}
          icon={Target}
          color="orange"
          format="percentage"
        />
      </div>

      {/* Performance Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Engagement Over Time
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={timeSeriesData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="opens" stroke="#3B82F6" strokeWidth={2} />
              <Line type="monotone" dataKey="clicks" stroke="#10B981" strokeWidth={2} />
              <Line type="monotone" dataKey="replies" stroke="#F59E0B" strokeWidth={2} />
              <Line type="monotone" dataKey="conversions" stroke="#EF4444" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Engagement Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="h-5 w-5" />
              Engagement Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <RechartsPieChart>
                <Pie
                  data={pieChartData}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="value"
                  label={({ name, percent }: { name: string; percent: number }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {pieChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </RechartsPieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              ROI Analysis
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Campaign Cost</span>
              <span className="font-semibold">{formatCurrency(currentCampaign.roi.cost)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Revenue Generated</span>
              <span className="font-semibold text-green-600">{formatCurrency(currentCampaign.roi.revenue)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Profit</span>
              <span className="font-semibold text-green-600">{formatCurrency(currentCampaign.roi.profit)}</span>
            </div>
            <div className="pt-2 border-t">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">ROI</span>
                <span className="text-lg font-bold text-green-600">
                  {formatPercentage(currentCampaign.roi.roiPercentage)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const DetailedTab = () => (
    <div className="space-y-6">
      {/* Detailed Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <MetricCard
          title="Delivered"
          value={currentCampaign.metrics.delivered}
          icon={CheckCircle}
          color="green"
        />
        <MetricCard
          title="Bounced"
          value={currentCampaign.metrics.bounced}
          icon={AlertTriangle}
          color="red"
        />
        <MetricCard
          title="Unsubscribed"
          value={currentCampaign.metrics.unsubscribed}
          icon={Users}
          color="orange"
        />
        <MetricCard
          title="Delivery Rate"
          value={currentCampaign.rates.deliveryRate}
          icon={Zap}
          color="blue"
          format="percentage"
        />
        <MetricCard
          title="Reply Rate"
          value={currentCampaign.rates.replyRate}
          icon={MessageSquare}
          color="purple"
          format="percentage"
        />
        <MetricCard
          title="Unsubscribe Rate"
          value={currentCampaign.rates.unsubscribeRate}
          icon={TrendingDown}
          color="red"
          format="percentage"
        />
      </div>

      {/* Performance Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Performance Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-sm font-medium">Delivery Rate</span>
                <span className="text-sm">{formatPercentage(currentCampaign.rates.deliveryRate)}</span>
              </div>
              <Progress value={currentCampaign.rates.deliveryRate} className="h-2" />
            </div>
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-sm font-medium">Open Rate</span>
                <span className="text-sm">{formatPercentage(currentCampaign.rates.openRate)}</span>
              </div>
              <Progress value={currentCampaign.rates.openRate} className="h-2" />
            </div>
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-sm font-medium">Click Rate</span>
                <span className="text-sm">{formatPercentage(currentCampaign.rates.clickRate)}</span>
              </div>
              <Progress value={currentCampaign.rates.clickRate} className="h-2" />
            </div>
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-sm font-medium">Conversion Rate</span>
                <span className="text-sm">{formatPercentage(currentCampaign.rates.conversionRate)}</span>
              </div>
              <Progress value={currentCampaign.rates.conversionRate} className="h-2" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Timeline */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Campaign Timeline
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
              <div>
                <p className="font-medium">Campaign Created</p>
                <p className="text-sm text-gray-600">
                  {new Date(currentCampaign.timeline.createdAt).toLocaleString()}
                </p>
              </div>
            </div>
            {currentCampaign.timeline.sentAt && (
              <div className="flex items-center gap-4">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <div>
                  <p className="font-medium">Campaign Sent</p>
                  <p className="text-sm text-gray-600">
                    {new Date(currentCampaign.timeline.sentAt).toLocaleString()}
                  </p>
                </div>
              </div>
            )}
            {currentCampaign.timeline.completedAt && (
              <div className="flex items-center gap-4">
                <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                <div>
                  <p className="font-medium">Campaign Completed</p>
                  <p className="text-sm text-gray-600">
                    {new Date(currentCampaign.timeline.completedAt).toLocaleString()}
                  </p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const ComparisonTab = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Campaign Comparison</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={activeCampaigns.map(campaign => ({
              name: campaign.name,
              openRate: campaign.rates.openRate,
              clickRate: campaign.rates.clickRate,
              conversionRate: campaign.rates.conversionRate * 10, // Scale for visibility
              replyRate: campaign.rates.replyRate
            }))}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="openRate" fill="#3B82F6" name="Open Rate %" />
              <Bar dataKey="clickRate" fill="#10B981" name="Click Rate %" />
              <Bar dataKey="replyRate" fill="#F59E0B" name="Reply Rate %" />
              <Bar dataKey="conversionRate" fill="#EF4444" name="Conversion Rate (x10)" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {activeCampaigns.map((campaign) => (
          <Card key={campaign.id} className={`cursor-pointer transition-all ${
            selectedCampaign?.id === campaign.id ? 'ring-2 ring-blue-500' : ''
          }`} onClick={() => setSelectedCampaign(campaign)}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">{campaign.name}</CardTitle>
                <Badge className={getStatusColor(campaign.status)}>
                  {getStatusText(campaign.status)}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Open Rate</p>
                  <p className="font-semibold">{formatPercentage(campaign.rates.openRate)}</p>
                </div>
                <div>
                  <p className="text-gray-600">Click Rate</p>
                  <p className="font-semibold">{formatPercentage(campaign.rates.clickRate)}</p>
                </div>
                <div>
                  <p className="text-gray-600">Conversions</p>
                  <p className="font-semibold">{campaign.metrics.converted}</p>
                </div>
                <div>
                  <p className="text-gray-600">ROI</p>
                  <p className="font-semibold text-green-600">
                    {formatPercentage(campaign.roi.roiPercentage)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Campaign Metrics
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Comprehensive performance analytics for your email campaigns
          </p>
        </div>
        <div className="flex items-center gap-4">
          <select
            value={timeframe}
            onChange={(e) => setTimeframe(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
          </select>
          {realTimeUpdates && (
            <Badge variant="outline" className="text-green-600 border-green-600">
              <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
              Live
            </Badge>
          )}
        </div>
      </div>

      {/* Campaign Selector */}
      {!campaignId && activeCampaigns.length > 1 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium">Campaign:</span>
              <select
                value={selectedCampaign?.id || ''}
                onChange={(e) => {
                  const campaign = activeCampaigns.find(c => c.id === e.target.value);
                  setSelectedCampaign(campaign || null);
                }}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm flex-1 max-w-xs"
              >
                {activeCampaigns.map((campaign) => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </option>
                ))}
              </select>
              <Badge className={getStatusColor(currentCampaign.status)}>
                {getStatusText(currentCampaign.status)}
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content */}
      <Tabs value={viewMode} onValueChange={(value: string) => setViewMode(value as any)}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="detailed">Detailed</TabsTrigger>
          <TabsTrigger value="comparison">Comparison</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <OverviewTab />
        </TabsContent>

        <TabsContent value="detailed" className="mt-6">
          <DetailedTab />
        </TabsContent>

        <TabsContent value="comparison" className="mt-6">
          <ComparisonTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CampaignMetrics; 